import { useEffect, useMemo, useState } from "react";
import { FileText, Trash2, UploadCloud, CheckCircle2, AlertCircle, Send, Pencil } from "lucide-react";
import client, { extractError } from "@/api";
import { CompanyMembershipAgreementPanel } from "@/components/CompanyMembershipAgreementPanel";

const DOC_LABELS = {
  GST_CERTIFICATE: "GST Certificate",
  PAN_DOCUMENT: "PAN Document",
  INCORPORATION_CERTIFICATE: "Incorporation / Registration Certificate",
  ADDRESS_PROOF: "Business Address Proof",
  OTHER: "Other Supporting Document",
};
const DOC_UPLOAD_ORDER = ["PAN_DOCUMENT", "INCORPORATION_CERTIFICATE", "ADDRESS_PROOF", "GST_CERTIFICATE", "OTHER"];

function emptyForm() {
  return {
    legal_name: "", brand_name: "", business_type: "", gstin: "", pan: "",
    website: "", business_description: "",
    contact_person_name: "", contact_designation: "", contact_email: "", contact_phone: "",
    country: "India", state_id: "", city_id: "", area: "", pin_code: "", registered_office_address: "",
    primary_category_id: "", primary_subcategory_id: "",
    selected_plan_id: "",
  };
}

function fromCompany(c) {
  return {
    legal_name: c.legal_name || "", brand_name: c.brand_name || "",
    business_type: c.business_type || "", gstin: c.gstin || "", pan: c.pan || "",
    website: c.website || "", business_description: c.business_description || "",
    contact_person_name: c.contact_person_name || "",
    contact_designation: c.contact_designation || "",
    contact_email: c.contact_email || "", contact_phone: c.contact_phone || "",
    country: c.country || "India", state_id: c.state_id || "", city_id: c.city_id || "",
    area: c.area || "", pin_code: c.pin_code || "", registered_office_address: c.registered_office_address || "",
    primary_category_id: c.primary_category_id || "",
    primary_subcategory_id: c.primary_subcategory_id || "",
    selected_plan_id: c.selected_plan_id || "",
  };
}

const STATUS_CONFIG = {
  DRAFT: { label: "Draft", tone: "draft", copy: "Complete your profile and upload the required documents, then submit for verification." },
  PENDING_VERIFICATION: { label: "Awaiting review", tone: "pending", copy: "Our operations team is reviewing your submission. Editing is locked until a decision is made." },
  VERIFIED: { label: "Verified", tone: "verified", copy: "Your company is verified. You will be able to trade once the marketplace opens." },
  REJECTED: { label: "Needs changes", tone: "rejected", copy: "Review the reviewer notes below, update your details or documents, and resubmit." },
  SUSPENDED: { label: "Suspended", tone: "suspended", copy: "Trading access has been paused. Existing history is preserved. Contact platform support for next steps." },
};

const EDITABLE_STATUSES = new Set(["DRAFT", "REJECTED"]);

export default function CompanyPage({ user, onCompanyChange }) {
  const [company, setCompany] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [uploading, setUploading] = useState("");
  const [uploadType, setUploadType] = useState("PAN_DOCUMENT");
  const [editing, setEditing] = useState(false);

  const loadReferences = async () => {
    const [s, c, bt, pl] = await Promise.all([
      client.get("/reference/states"),
      client.get("/reference/categories"),
      client.get("/reference/business-types"),
      client.get("/public/subscription-plans"),
    ]);
    setStates(s.data);
    setCategories(c.data);
    setBusinessTypes(bt.data);
    setPlans(Array.isArray(pl.data) ? pl.data : []);
  };

  const loadCompany = async () => {
    const res = await client.get("/companies/me");
    setCompany(res.data || null);
    if (res.data) {
      setForm(fromCompany(res.data));
      if (res.data.state_id) {
        const cityRes = await client.get(`/reference/cities?state_id=${res.data.state_id}`);
        setCities(cityRes.data);
      }
    }
    return res.data;
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await loadReferences();
        await loadCompany();
      } catch (err) {
        setError(extractError(err, "Unable to load your company workspace."));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!form.state_id) { setCities([]); return; }
    let ignore = false;
    client.get(`/reference/cities?state_id=${form.state_id}`)
      .then((r) => { if (!ignore) setCities(r.data); })
      .catch(() => { if (!ignore) setCities([]); });
    return () => { ignore = true; };
  }, [form.state_id]);

  const activeCategory = useMemo(
    () => categories.find((c) => c.id === form.primary_category_id),
    [categories, form.primary_category_id],
  );

  const change = (key) => (event) => {
    const value = event.target.value;
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "state_id") next.city_id = "";
      if (key === "primary_category_id") next.primary_subcategory_id = "";
      return next;
    });
  };

  const isNew = !company;
  const status = company?.status;
  const inEditableState = !company || EDITABLE_STATUSES.has(status);
  const showForm = isNew || (inEditableState && editing);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true); setError(""); setMessage("");
    try {
      const payload = {
        ...form,
        gstin: form.gstin?.trim().toUpperCase() || null,
        website: form.website || null,
        selected_plan_id: form.selected_plan_id || null,
      };
      const response = isNew
        ? await client.post("/companies", payload)
        : await client.patch("/companies/me", payload);
      setCompany(response.data);
      setEditing(false);
      setMessage(isNew ? "Draft saved. Upload documents next, then submit for verification." : "Details saved.");
      onCompanyChange?.(response.data);
    } catch (err) {
      setError(extractError(err, "Unable to save your company."));
    } finally {
      setSaving(false);
    }
  };

  const submitForVerification = async () => {
    setSubmitting(true); setError(""); setMessage("");
    try {
      const response = await client.post("/companies/me/submit");
      setCompany(response.data);
      onCompanyChange?.(response.data);
      setMessage("Submitted for verification. Our team will review your company shortly.");
    } catch (err) {
      setError(extractError(err, "Unable to submit for verification."));
    } finally {
      setSubmitting(false);
    }
  };

  const uploadDocument = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setUploading(uploadType); setError(""); setMessage("");
    try {
      const data = new FormData();
      data.append("file", file);
      await client.post(`/companies/me/documents?document_type=${uploadType}`, data);
      await loadCompany();
      setMessage(`${DOC_LABELS[uploadType]} uploaded.`);
    } catch (err) {
      setError(extractError(err, "Document upload failed."));
    } finally {
      setUploading("");
    }
  };

  const removeDocument = async (docId) => {
    if (!window.confirm("Remove this document? You can upload a replacement afterwards.")) return;
    try {
      await client.delete(`/companies/me/documents/${docId}`);
      await loadCompany();
      setMessage("Document removed.");
    } catch (err) {
      setError(extractError(err, "Unable to remove document."));
    }
  };

  const viewDocument = async (doc) => {
    try {
      const res = await client.get(`/companies/me/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setError(extractError(err, "Unable to open document."));
    }
  };

  if (loading) return <div className="loading-block" data-testid="company-loading">Loading company workspace…</div>;

  // ------------------ STATUS VIEW (existing company, not editing) ------------------
  if (company && !showForm) {
    const config = STATUS_CONFIG[status] || {};
    const docs = company.documents || [];
    const requiredTypes = company.required_document_types || ["PAN_DOCUMENT", "INCORPORATION_CERTIFICATE", "ADDRESS_PROOF"];
    const missingDocs = company.missing_required_documents || [];
    const canSubmit = company.can_submit_for_verification && !!company.selected_plan_id;
    const canEdit = EDITABLE_STATUSES.has(status);

    // Build a slot per required + optional document type
    const orderedTypes = DOC_UPLOAD_ORDER;
    const docsByType = docs.reduce((acc, d) => { acc[d.document_type] = acc[d.document_type] || []; acc[d.document_type].push(d); return acc; }, {});

    return (
      <div className="company-status-view" data-testid="company-status-view">
        <div className={`status-banner status-${config.tone}`} data-testid="company-status-banner">
          <div className="status-icon">{status === "VERIFIED" ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}</div>
          <div>
            <p className="eyebrow">COMPANY STATUS</p>
            <h2 data-testid="company-status-label">{config.label}</h2>
            <p>{config.copy}</p>
          </div>
        </div>

        {status === "REJECTED" && company.rejection_reason && (
          <div className="review-callout rejected" data-testid="rejection-reason">
            <p className="eyebrow">REJECTION REASON</p>
            <p>{company.rejection_reason}</p>
            {company.admin_review_notes && <p className="muted small">Reviewer notes: {company.admin_review_notes}</p>}
            <p className="muted small">Update the flagged details or documents below, then click <strong>Submit for verification</strong> again.</p>
          </div>
        )}
        {status === "SUSPENDED" && company.suspension_reason && (
          <div className="review-callout suspended" data-testid="suspension-reason">
            <p className="eyebrow">SUSPENSION REASON</p>
            <p>{company.suspension_reason}</p>
            {company.admin_review_notes && <p className="muted small">Notes: {company.admin_review_notes}</p>}
          </div>
        )}

        <section className="detail-grid">
          <article><p className="eyebrow">LEGAL NAME</p><p data-testid="detail-legal-name">{company.legal_name}</p></article>
          <article><p className="eyebrow">BRAND</p><p>{company.brand_name}</p></article>
          <article><p className="eyebrow">BUSINESS TYPE</p><p>{company.business_type}</p></article>
          <article><p className="eyebrow">GSTIN</p><p>{company.gstin || "—"}</p></article>
          <article><p className="eyebrow">PAN</p><p>{company.pan}</p></article>
          <article><p className="eyebrow">CONTACT</p><p>{company.contact_person_name}<br /><small>{company.contact_designation}</small></p></article>
          <article><p className="eyebrow">EMAIL / PHONE</p><p>{company.contact_email}<br /><small>{company.contact_phone}</small></p></article>
          <article><p className="eyebrow">LOCATION</p><p>{company.area}, {company.pin_code}</p></article>
          <article className="wide"><p className="eyebrow">REGISTERED OFFICE ADDRESS</p><p data-testid="company-registered-office-address">{company.registered_office_address || "—"}</p></article>
          <article><p className="eyebrow">SUBMITTED</p><p>{company.submitted_at ? new Date(company.submitted_at).toLocaleString() : "Not submitted"}</p></article>
          {company.selected_plan_id && (
            <article data-testid="detail-selected-plan"><p className="eyebrow">SELECTED PLAN</p><p>{(plans.find((p) => p.id === company.selected_plan_id)?.name) || company.selected_plan_id}</p></article>
          )}
          {company.payment_status && company.payment_status !== "NOT_APPLICABLE" && (
            <article data-testid="detail-payment-status"><p className="eyebrow">PAYMENT STATUS</p><p>{company.payment_status === "PENDING" ? "Payment pending — the admin will contact you shortly." : company.payment_status === "RECEIVED" ? "Payment received — account activated." : company.payment_status}</p></article>
          )}
        </section>

        <CompanyMembershipAgreementPanel />

        <section className="documents-section" data-testid="documents-section">
          <div className="section-heading">
            <div><p className="eyebrow">VERIFICATION DOCUMENTS</p><h3>Uploaded records</h3></div>
            {canEdit && (
              <div className="upload-row">
                <select value={uploadType} onChange={(e) => setUploadType(e.target.value)} data-testid="document-type-select">
                  {orderedTypes.map((k) => (
                    <option key={k} value={k}>
                      {DOC_LABELS[k]}{requiredTypes.includes(k) ? " *" : ""}
                    </option>
                  ))}
                </select>
                <label className="ghost-button" data-testid="upload-document-button">
                  {uploading ? "Uploading…" : (<><UploadCloud size={16} /> Upload</>)}
                  <input type="file" accept=".pdf,.png,.jpg,.jpeg" hidden onChange={uploadDocument} disabled={!!uploading} />
                </label>
              </div>
            )}
          </div>
          <p className="muted small">Accepted formats: PDF, JPG, PNG. Fields marked * are required for verification.</p>

          <ul className="document-checklist" data-testid="document-checklist">
            {orderedTypes.map((type) => {
              const items = docsByType[type] || [];
              const isRequired = requiredTypes.includes(type);
              const isMissing = isRequired && items.length === 0;
              return (
                <li key={type} className={isMissing ? "missing" : items.length ? "present" : "optional"} data-testid={`doc-slot-${type}`}>
                  <div className="slot-heading">
                    <strong>{DOC_LABELS[type]}{isRequired ? " *" : ""}</strong>
                    {isMissing && <span className="chip chip-missing">Required</span>}
                    {!isMissing && items.length > 0 && <span className="chip chip-uploaded">Uploaded</span>}
                    {!isRequired && items.length === 0 && <span className="chip chip-optional">Optional</span>}
                  </div>
                  {items.map((doc) => (
                    <div className="doc-row" key={doc.id} data-testid={`document-row-${doc.id}`}>
                      <FileText size={14} />
                      <div className="doc-meta">
                        <small className="doc-filename">{doc.file_name}</small>
                        <small className="muted">{(doc.file_size / 1024).toFixed(1)} KB · uploaded {new Date(doc.created_at).toLocaleDateString()}</small>
                      </div>
                      <button type="button" className="link-button" data-testid={`view-document-${doc.id}`} onClick={() => viewDocument(doc)}>View</button>
                      {canEdit && (
                        <button type="button" className="danger-button" data-testid={`remove-document-${doc.id}`} onClick={() => removeDocument(doc.id)}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </li>
              );
            })}
          </ul>
        </section>

        {message && <div className="form-success" data-testid="company-message">{message}</div>}
        {error && <div className="form-error" data-testid="company-error">{error}</div>}

        <div className="detail-actions">
          {canEdit && (
            <button type="button" className="ghost-button" data-testid="edit-company-button" onClick={() => { setEditing(true); setMessage(""); setError(""); }}>
              <Pencil size={14} /> Edit details
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              className="primary-button"
              data-testid="submit-verification-button"
              onClick={submitForVerification}
              disabled={!canSubmit || submitting}
              title={!canSubmit ? "Please select a subscription plan and complete your profile & documents before submitting." : undefined}
            >
              <Send size={14} /> {submitting ? "Submitting…" : "Submit for verification"}
            </button>
          )}
        </div>
        {canEdit && !canSubmit && (missingDocs.length > 0 || !company.selected_plan_id) && (
          <p className="form-error small" data-testid="submit-blocked-message">
            {!company.selected_plan_id
              ? "Please select a subscription plan before submitting for verification."
              : "Please complete your company profile and upload all required verification documents before submitting."}
          </p>
        )}
      </div>
    );
  }

  // ------------------ FORM VIEW (create or edit) ------------------
  return (
    <form className="company-form" onSubmit={submit} data-testid="company-form">
      <p className="muted">{isNew ? "Complete the sections below to save your company as a draft. You will upload documents and submit for verification after saving. All fields marked * are required." : "Update the flagged details, then use Submit for verification when ready. All fields marked * are required."}</p>

      <fieldset data-testid="fieldset-subscription-plan"><legend>SUBSCRIPTION PLAN</legend>
        <p className="muted small">Choose the subscription plan you'd like to activate after your company is verified. You can change this while your application is a draft or awaiting corrections.</p>
        <div className="plan-picker" data-testid="plan-picker">
          {plans.length === 0 && (
            <p className="muted small" data-testid="plan-picker-empty">No subscription plans are currently available. Please try again shortly.</p>
          )}
          {plans.map((p) => (
            <label key={p.id} className={`plan-option${form.selected_plan_id === p.id ? " selected" : ""}`} data-testid={`plan-option-${p.id}`}>
              <input
                type="radio"
                name="selected_plan_id"
                value={p.id}
                checked={form.selected_plan_id === p.id}
                onChange={change("selected_plan_id")}
                data-testid={`plan-radio-${p.id}`}
                required
              />
              <span className="plan-body">
                <strong>{p.name}{p.is_recommended ? " · Recommended" : ""}</strong>
                <span className="plan-price">&#8377;{Number(p.subscription_fee || 0).toLocaleString("en-IN")}</span>
                <span className="muted small">Initial Trade Credits: &#8377;{Number(p.initial_credit_limit || 0).toLocaleString("en-IN")} · Valid {p.validity_days} days</span>
                {p.description && <span className="muted small">{p.description}</span>}
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset><legend>LEGAL / BUSINESS INFORMATION</legend>
        <div className="grid-2">
          <label>Legal company name *<input required data-testid="input-legal-name" value={form.legal_name} onChange={change("legal_name")} placeholder="Acme Traders Pvt Ltd" /></label>
          <label>Brand / trading name *<input required data-testid="input-brand-name" value={form.brand_name} onChange={change("brand_name")} placeholder="Acme" /></label>
          <label>Business type *
            <select required data-testid="input-business-type" value={form.business_type} onChange={change("business_type")}>
              <option value="">Select…</option>
              {businessTypes.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
          </label>
          <label>GSTIN (optional)<input data-testid="input-gstin" value={form.gstin} onChange={change("gstin")} placeholder="27ABCDE1234F1Z5" maxLength={15} /></label>
          <label>PAN *<input required data-testid="input-pan" value={form.pan} onChange={change("pan")} placeholder="ABCDE1234F" maxLength={10} /></label>
          <label>Company website<input type="url" data-testid="input-website" value={form.website} onChange={change("website")} placeholder="https://" /></label>
        </div>
        <label>Business description *<textarea required minLength={10} data-testid="input-description" value={form.business_description} onChange={change("business_description")} rows={4} placeholder="What does your company do? Products, services, industries served." /></label>
      </fieldset>

      <fieldset><legend>PRIMARY CONTACT</legend>
        <div className="grid-2">
          <label>Contact person name *<input required data-testid="input-contact-name" value={form.contact_person_name} onChange={change("contact_person_name")} /></label>
          <label>Designation *<input required data-testid="input-contact-designation" value={form.contact_designation} onChange={change("contact_designation")} placeholder="Director / Founder" /></label>
          <label>Contact email *<input required type="email" data-testid="input-contact-email" value={form.contact_email} onChange={change("contact_email")} /></label>
          <label>Contact phone *<input required data-testid="input-contact-phone" value={form.contact_phone} onChange={change("contact_phone")} placeholder="+91 98123 45670" /></label>
        </div>
      </fieldset>

      <fieldset><legend>BUSINESS LOCATION</legend>
        <div className="grid-2">
          <label>Country *<input required data-testid="input-country" value={form.country} onChange={change("country")} /></label>
          <label>State *
            <select required data-testid="input-state" value={form.state_id} onChange={change("state_id")}>
              <option value="">Select state…</option>
              {states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label>City *
            <select required data-testid="input-city" value={form.city_id} onChange={change("city_id")} disabled={!form.state_id}>
              <option value="">{form.state_id ? "Select city…" : "Choose state first"}</option>
              {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label>Area / locality *<input required data-testid="input-area" value={form.area} onChange={change("area")} placeholder="Andheri East" /></label>
          <label>PIN code *<input required data-testid="input-pin" value={form.pin_code} onChange={change("pin_code")} maxLength={6} placeholder="400001" /></label>
        </div>
        <label>Registered office address *<textarea required minLength={5} maxLength={2000} data-testid="input-registered-office-address" value={form.registered_office_address} onChange={change("registered_office_address")} rows={3} placeholder="Registered office address as per official records" /></label>
      </fieldset>

      <fieldset><legend>BUSINESS CLASSIFICATION</legend>
        <div className="grid-2">
          <label>Main business category *
            <select required data-testid="input-category" value={form.primary_category_id} onChange={change("primary_category_id")}>
              <option value="">Select category…</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label>Subcategory *
            <select required data-testid="input-subcategory" value={form.primary_subcategory_id} onChange={change("primary_subcategory_id")} disabled={!activeCategory}>
              <option value="">{activeCategory ? "Select subcategory…" : "Choose category first"}</option>
              {activeCategory?.subcategories?.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
        </div>
      </fieldset>

      {error && <div className="form-error" data-testid="company-form-error">{error}</div>}
      {message && <div className="form-success" data-testid="company-form-message">{message}</div>}
      <div className="form-actions">
        {!isNew && (
          <button type="button" className="ghost-button" data-testid="cancel-edit-button" onClick={() => { setEditing(false); setForm(fromCompany(company)); setError(""); setMessage(""); }}>
            Cancel
          </button>
        )}
        <button type="submit" className="primary-button" data-testid="save-company-button" disabled={saving}>
          {saving ? "Saving…" : isNew ? "Save draft" : "Save changes"}
        </button>
      </div>
      <p className="muted small">Documents will be uploaded and the company submitted for verification from the next screen.</p>
    </form>
  );
}
